import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { isEmpty, isUndefined, omitBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useQueryClient } from 'react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import * as SvgIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import CategoriesSlider from 'components/UI/CategoriesSlider/CategoriesSlider'
import FriendRequestModal from 'components/UI/Modal/FriendRequest/FriendRequestModal'
import { PostPlaceholder } from 'components-ref/molecules'
import { FeedNav } from 'components-ref/organisms/feedNav/FeedNav'
import { useModalContext } from 'context/modalContext'
import { usePreviewContext } from 'context/previewContext'
import { useProfileContext } from 'context/profileContext'
import { useUserContext } from 'context/userContext'
import { usePostsFeed } from 'helpers/hooks'
import { useImpressionsTracker } from 'helpers/hooks/feed/useImpressionsTracker'
import {
  isPostQueryKey,
  updatePostInQueryData
} from 'helpers/post/updatePostInFeedCache'
import { getSinglePost } from 'services/endpoints/post'
import { POST_FINISHED } from 'services/notifications/notificationTypes'
import { type FeedType, useAccessToken, useFeedTypeStore, useUserProfile } from 'store'
import { useGlobalStore } from 'store/global'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'
import { type IPost } from 'types/interfaces/ITypes'

import VirtualizedPostsFeedList from './VirtualizedPostsFeedList'

import 'react-loading-skeleton/dist/skeleton.css'
import './_postsFeed.scss'

interface PostFeedProps {
  feedNav: FeedType[]
  id: string
  user: any
  isFan?: boolean
  setSubscribeModalOpen: any
  username: string
  onInitialFeedSettled?: () => void
}


export const PostsFeed = ({ feedNav, id, user, setSubscribeModalOpen, onInitialFeedSettled }: PostFeedProps) => {
  const { accountSetupPopup } = useGlobalStore()
  const location = useLocation()
  const skipProfileFeedRefetch =
  location.state?.skipProfileFeedRefetch === true
  const [categoriesActive, setCategoriesActive] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [initialCategorySlide, setInitialCategorySlide] = useState(0)

  const feedRef = useRef<HTMLDivElement | null>(null)
  const profileViewSentRef = useRef<string | null>(null)

  const [skeletonGroups, setSkeletonGroups] = useState(2)
  const queryClient = useQueryClient()
  const feed = useFeedTypeStore(state => state.type)
  const modalData = useModalContext()
  const { notificationsChannel } = useUserContext()
  const userData = useUserProfile()
  const navigate = useNavigate()
  const { setProfileMarker } = useProfileContext()
  const isAuthenticated = useAccessToken() !== ''
  const { addDebounceImpression } = useImpressionsTracker()

  const parsedId = typeof id === 'string' ? id : String(id)
  const isOwnProfile = userData?.id === id

  const friendsStatus =
    (user?.friends_status ?? user?.friendsStatus ?? user?.friends ?? 'Not friend') as
      | 'Friends'
      | 'Pending'
      | 'Not friend'

  const isFriend = friendsStatus === 'Friends'
  const isPendingFriend = friendsStatus === 'Pending'
  const isNotFriend = friendsStatus === 'Not friend'

  const {
    getFeed,
    getFeedEntry,
    setFeed,
    updatePost
  } = useProfileFeedCacheStore()

  const cacheKey = useMemo(() => {
    const username = user?.username ?? ''
    const type = feed || 'all'
    const cat = selectedCategory || ''
    return `${username}:${type}:${cat}`
  }, [user?.username, feed, selectedCategory])

  const cachedEntry = getFeedEntry(cacheKey)
  const cachedPosts = getFeed(cacheKey)
  
  const lastPersistedRef = useRef<{
    key: string
    data: unknown
  } | null>(null)


  useLayoutEffect(() => {
    const calculateSkeletonGroups = () => {
      const containerTop = feedRef.current?.getBoundingClientRect().top ?? 0
      const availableHeight = window.innerHeight - containerTop

      // one "post"
      const postHeight =
        220 + // media
        10 +  // spacing between media + caption
        50    // caption

      // spacing between posts inside group
      const postGap = 12

      // spacing between groups
      const groupPadding = 24

      // 2 posts per group
      const groupHeight =
        postHeight * 2 +
        postGap +
        groupPadding

      const groups = Math.ceil(availableHeight / groupHeight) + 1

      setSkeletonGroups(Math.max(1, groups))
    }

    calculateSkeletonGroups()

    window.addEventListener('resize', calculateSkeletonGroups)

    return () => {
      window.removeEventListener('resize', calculateSkeletonGroups)
    }
  }, [])


  const isSubscribed = user?.isSubscribed === true || isOwnProfile || isFriend

const {
  data,
  error,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  status,
  isLoading
} = usePostsFeed(
  parsedId,
  { type: feed, media_category: selectedCategory },
  cachedPosts,
  isSubscribed,
  cachedEntry?.updatedAt,
  {
    refetchOnMount: !skipProfileFeedRefetch
  }
)

const feedPosts = useMemo<IPost[]>(() => {
  return (
    data?.pages?.flatMap(
      (page: any) => page?.page?.data?.data ?? []
    ) ?? []
  ).filter(
    (post: IPost) =>
      String(post.user_id) === String(id)
  )
}, [data, id])
  
  const { setInitialData, setFilters, selectedPostData, setSelectedPostData } = usePreviewContext()
  const { t } = useTranslation()

  if (error) {
    console.log(error)
  }

  const ownCategories = Array.isArray(userData?.all_media_categories)
    ? userData?.all_media_categories
    : []

  const viewerCategories = Array.isArray(user?.media_categories) ? user.media_categories : []

  const categoriesToShow = isOwnProfile
    ? ownCategories
    : (user?.isSubscribed === true || isFriend)
      ? viewerCategories
      : []

  const showFeedNav = isSubscribed 
  const showCategoryControls = isAuthenticated && (isOwnProfile || categoriesToShow.length > 0)
  const showSecondaryNav =
  isAuthenticated && (isOwnProfile || categoriesToShow.length > 0)

  useEffect(() => {
    if (!showCategoryControls && categoriesActive) {
      setCategoriesActive(false)
    }
  }, [showCategoryControls, categoriesActive])

// FLIP PHOTO/VIDEO POSTS WHEN POST-FINISHED / PHOTO PROCESSSING EVENTS

  useEffect(() => {
    if (!notificationsChannel) {
      return
    }

  const handler = async (data: any) => {
    const rawPostId =
      data?.post ??
      data?.post_id

    const postId =
      typeof rawPostId === 'string'
        ? rawPostId
        : rawPostId != null
          ? String(rawPostId)
          : ''

    if (!postId) {
      return
    }

    try {
      const response = await getSinglePost(postId)

      const freshPost =
        response?.data?.data ??
        response?.data ??
        response

      if (!freshPost?.id) {
        return
      }

      const completedPost = {
        ...freshPost,
        processing: freshPost.encode_status === 'processing'
      }

      // Persistent Zustand cache
      updatePost(completedPost)

      // React Query cache
      queryClient.setQueriesData(
        {
          predicate: query =>
            isPostQueryKey(query.queryKey)
        },
        old => updatePostInQueryData(old, completedPost)
      )

      if (String(selectedPostData?.id) === String(postId)) {
        setSelectedPostData(completedPost)
      }
    } catch {
      // Ignore fetch failures.
    }
  }

    notificationsChannel.bind(
      'post-finished',
      handler
    )

    notificationsChannel.bind(
      'photo-processing-finished',
      handler
    )

    return () => {
      notificationsChannel.unbind(
        'post-finished',
        handler
      )

      notificationsChannel.unbind(
        'photo-processing-finished',
        handler
      )
    }
}, [
  notificationsChannel,
  selectedPostData?.id,
  setSelectedPostData,
  updatePost,
])

  const previewInitSigRef = useRef('')

  useEffect(() => {
      if (!isAuthenticated || !isSubscribed) {
      return
    }

    const effectiveType = ['premium', 'videos'].includes(feed) ? feed : 'all'
    const sig = `${id}:${parsedId}:${effectiveType}:${feed}:${selectedCategory}`
    if (previewInitSigRef.current === sig) return
    previewInitSigRef.current = sig

    const cleaned = omitBy(
      { type: feed, media_category: selectedCategory },
      value => value === '' || isUndefined(value) || isEmpty(value)
    )

    const queryKey = ['getFeed', parsedId, cleaned] as const

    setInitialData(id, effectiveType, 'profile', { type: feed, media_category: selectedCategory }, queryKey)
    setFilters(feed, selectedCategory)
  }, [isAuthenticated, id, parsedId, feed, selectedCategory, setInitialData, setFilters])

useEffect(() => {
  if (!isSubscribed || !data || isLoading || isFetching) {
    return
  }

  if (profileViewSentRef.current === parsedId) {
    return
  }

  profileViewSentRef.current = parsedId
  onInitialFeedSettled?.()
}, [
  isSubscribed,
  data,
  isLoading,
  isFetching,
  parsedId,
  onInitialFeedSettled
])


  useEffect(() => {
    if (!data?.pages?.length || isLoading || isFetching) {
      return
    }

    if (
      lastPersistedRef.current?.key === cacheKey &&
      lastPersistedRef.current?.data === data
    ) {
      return
    }

    const lastPage = data.pages[data.pages.length - 1]

    const nextCursor =
      lastPage?.nextCursor ?? null

    lastPersistedRef.current = {
      key: cacheKey,
      data
    }

    setFeed(cacheKey, feedPosts, {
      nextPageParam: nextCursor,
      hasNextPage: !!nextCursor
    })
  }, [
    data,
    cacheKey,
    feedPosts,
    isLoading,
    isFetching,
    setFeed
  ])

  useEffect(() => {
    if (!selectedPostData) return
    const updated = feedPosts.find(p => p.id === selectedPostData.id)
    if (updated) setSelectedPostData(updated)
  }, [feedPosts, selectedPostData, setSelectedPostData])

  const addFriend = () => {
    modalData.addModal(
      t('friendRequest'),
      <FriendRequestModal
        id={user.id}
        online={user?.online_status}
        avatar={user?.avatar.url || user?.cropped_avatar.url}
        display_name={user?.display_name}
        username={user?.username}
        follower_count={user?.follower_count}
        friend_count={user?.friend_count}
        close={() => modalData.clearModal()}
      />
    )
  }

  const dataQuery = useMemo(() => {
    return [
      'getFeed',
      parsedId,
      omitBy(
        { type: feed, media_category: selectedCategory },
        value => value === '' || isUndefined(value) || isEmpty(value)
      ),
    ] as const
  }, [parsedId, feed, selectedCategory])

  const loginAndSubscribe = () => {
    setProfileMarker && setProfileMarker({ profileId: user.id, fromAuth: true })
    navigate('/')
  }

  const topNav = showFeedNav ? (
    <>
      <div className="profilefeed__nav">
        <FeedNav nav={feedNav} />
        {showCategoryControls && (
          <div className="profilefeed__nav--secondary">
            <IconButton
              icon={<SvgIcons.IconCategories color={categoriesActive ? '#2894FF' : ''} />}
              size="medium"
              type="outline"
              clickFn={() => setCategoriesActive(!categoriesActive)}
            />
          </div>
        )}
      </div>

{showSecondaryNav && categoriesActive && (
  <div className="profilefeed_nav profilefeed_nav--secondary">
    <CategoriesSlider
      categoriesOpen={categoriesActive}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      initialCategorySlide={initialCategorySlide}
      setInitialCategorySlide={setInitialCategorySlide}
      categories={categoriesToShow}
      ownProfile={isOwnProfile}
    />
  </div>
)}
    </>
  ) : null

if (!isAuthenticated) {
  return (
    <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
      <PostPlaceholder
        user={user}
        btnFn={() => setSubscribeModalOpen(true)}
        btnText={t('subscribe')}
      />
    </div>
  )
}

const isInitialLoading =
  feedPosts.length === 0 &&
  isLoading

const showEmptyState =
  status === 'success' &&
  !isFetching &&
  feedPosts.length === 0
  
  if (isInitialLoading) {
    return (
      <div
        ref={feedRef}
        className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''} skeleton`}
      >
        {topNav}

        {Array.from({ length: skeletonGroups }).map((_, groupIndex) => (
          <div
            className="profilefeed__skeletonGroup"
            key={groupIndex}
            style={{
              marginBottom: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {[0, 1].map(postIndex => (
              <div key={postIndex}>
                <Skeleton borderRadius={20} height={220} />

                <Skeleton
                  style={{ marginTop: 10 }}
                  borderRadius={20}
                  height={50}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (userData?.role === 'model' && !user.isSubscribed && isNotFriend && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={addFriend} user={user} btnText={t('addFriend')} />
      </div>
    )
  }

  if (userData?.role === 'model' && !user.isSubscribed && isPendingFriend && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={() => setSubscribeModalOpen(true)} user={user} btnText={t('subscribe')} />
      </div>
    )
  }

  if ((userData?.role === 'fan' || userData?.role === 'model') && !isSubscribed && user.id !== userData?.id) {
    return (
      <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
        {topNav}
        <PostPlaceholder btnFn={() => setSubscribeModalOpen(true)} user={user} btnText={t('subscribe')} />
      </div>
    )
  }



const addFirstPostText =
  feed === 'videos'
    ? t('addYourFirstVideo')
    : feed === 'premium'
      ? t('addYourFirstPremiumPost')
      : feed === 'reels'
        ? t('addYourFirstReel')
        : t('addYourFirstPhotoOrVideo')


  return (
    <div className={`profilefeed ${accountSetupPopup.visible ? 'extraPad' : ''}`}>
      {topNav}

      {feedPosts.length > 0 && (
         <div className="profilefeed__content">
        <VirtualizedPostsFeedList
          // key={cacheKey}
          feedPosts={feedPosts}
          userId={user.id}
          userData={userData}
          addDebounceImpression={addDebounceImpression}
          dataQuery={dataQuery as any}
          fetchNextPage={fetchNextPage}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={!!isFetchingNextPage}
estimateRowPx={
  feed === 'reels'
    ? 210
    : window.innerWidth >= 1200
      ? 312
      : 292
}
          rowGapPx={12}
          overscan={2}
          prefetchAhead={feed === 'reels' ? 2 : 1}
          fetchThreshold={4}
          layout={feed === 'reels' ? 'grid' : 'list'}
        />

          <div>{isFetchingNextPage ? <></> : null}</div>
          <div>{isFetching && !isFetchingNextPage ? <></> : null}</div>
        </div>
      )}

      {showEmptyState && (
        <>
          {userData?.id === user.id ? (
            selectedCategory !== '' ? (
              <div className="profilefeed__noposts">{t('noPostsInThisCategory')}</div>
            ) : (
              <Link to={'/new/post/create'}>
                <div className="profilefeed-model-no-posts">
                  <div className="profilefeed-model-no-posts-background"></div>
                  <div className="profilefeed-model-no-posts-inner">
                    <div className="profilefeed-model-no-posts-add-button">
                      <SvgIcons.IconPlusInBox color="#ffffff" width="28" height="28" />
                    </div>
                    <div className="profilefeed-model-no-posts-text">
                      {addFirstPostText}
                    </div>
                  </div>
                </div>
              </Link>
            )
          ) : (
            <div className="profilefeed__noposts">
              {feed === 'videos'
                ? t('userHasNotSharedAnyVideos')
                : feed === 'premium'
                  ? t('userHasNotSharedAnyPremiumVideos')
                  : t('userHasntPostedAnythingYet')}
            </div>
          )}
        </>
      )}

      <div className="edit__post__nav--wrapper"></div>
    </div>
  )
}